import React from 'react';
import StyleConstants from '../../../core/styles.constants';
import { isAsDrawn, type EditableFloorPlan } from '../models/editor-feature';
import { backdropDrawOrder } from '../utils/backdrop-order';
import { computeBoundingBox, multiPolygonToPath, toScreen, type Projection } from '../utils/projection';

interface Props {
  readonly floorPlan: EditableFloorPlan;
  readonly projection: Projection;
  readonly showFloor: boolean;
  readonly showRooms: boolean;
  readonly showBorder: boolean;
}

/** The slab a plan is drawn on: near-white, so the walls standing on it are what the eye reads. */
const PLATE_FILL = '#F4F4F1';
const PLATE_STROKE = '#B4B4AE';

/** Walls and structural fragments: solid enough to read as material at a glance. */
const WALL_FILL = '#CFCFC9';
const WALL_STROKE = '#6E6E68';

/** What is kept as drawn from a traced picture: its ink, softened so the plan reads over it. */
const AS_DRAWN_FILL = '#5B616B';

/**
 * Rooms sit in front of the walls, so their fill is translucent and their outline is not.
 * The outline is what marks the room out; the fill only has to tint what it covers.
 */
const ROOM_FILL = '#DCE6F5';
const ROOM_FILL_OPACITY = 0.45;
const ROOM_STROKE = '#5B7FB0';

/**
 * The parts of a floorplan nobody can edit: the building outline, the walls and structural
 * fragments, and the room polygons.
 *
 * Memoised and non-interactive on purpose. This is by far the largest thing on the surface
 * — the biggest stored plan has 418 floor features — and it never changes while the admin
 * drags a desk around, so re-rendering it on every pointer move would make the editor feel
 * heavy for no reason. `pointerEvents: none` also keeps it from swallowing clicks meant for
 * the desks drawn on top of it.
 */
const LayerBackdrop = ({ floorPlan, projection, showFloor, showRooms, showBorder }: Props): React.JSX.Element => {
  // Size decides painting order and fill, so a floor plate ends up under the walls rather than
  // over them. Which of the two a stored layer happens to produce is otherwise pure luck: the
  // plans that read correctly list their plate first, and the ones that came up blank listed
  // it last.
  const asDrawn = floorPlan.floorBackdrop.filter(isAsDrawn);
  const floorFeatures = backdropDrawOrder(floorPlan.floorBackdrop.filter((feature) => !isAsDrawn(feature)), computeBoundingBox(floorPlan));

  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* What is kept as drawn from a traced picture, under the rest and never the plate. */}
      {showFloor
        && asDrawn.map((feature) => (
          <path
            key={feature.id}
            data-layer="floor"
            d={multiPolygonToPath(feature.coordinates, projection)}
            fill={AS_DRAWN_FILL}
            fillRule="evenodd"
            stroke="none"
          />
        ))}

      {showFloor
        && floorFeatures.map(({ feature, isPlate }) => (
          <path
            key={feature.id}
            data-layer="floor"
            d={multiPolygonToPath(feature.coordinates, projection)}
            fill={isPlate ? PLATE_FILL : WALL_FILL}
            stroke={isPlate ? PLATE_STROKE : WALL_STROKE}
            strokeWidth={isPlate ? 0.5 : 0.7}
          />
        ))}

      {/* Floor detail drawn as lines rather than shapes. One plan keeps its entire floor
          drawing this way, and skipping it left that plan with rooms and an outline and
          nothing in between. */}
      {showFloor
        && floorPlan.floorLines.map((feature) => (
          <g key={feature.id}>
            {feature.paths.map((path, index) => (
              <polyline
                // Index is the only thing distinguishing one run of a line feature from
                // another; they have no identity of their own in the stored data.
                // eslint-disable-next-line react/no-array-index-key
                key={`${feature.id}-${index}`}
                data-layer="floor"
                points={path.map((point) => {
                  const { x, y } = toScreen(point, projection);
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke={WALL_STROKE}
                strokeWidth={0.7}
              />
            ))}
          </g>
        ))}

      {/* Rooms last of the backdrop, so a room always reads in front of the walls it is
          bounded by. Only the fill is held back: at full strength it buried the structure
          underneath, which is the state the plans with the most rooms were in. A translucent
          fill with a solid outline puts the room in front without painting the building out
          of it. */}
      {showRooms &&
        floorPlan.rooms.map((feature) => (
          <path
            key={feature.id}
            data-layer="room"
            d={multiPolygonToPath(feature.coordinates, projection)}
            fill={ROOM_FILL}
            fillOpacity={ROOM_FILL_OPACITY}
            stroke={ROOM_STROKE}
            strokeWidth={1}
          />
        ))}

      {showBorder &&
        floorPlan.border.map((feature) => (
          <path
            key={feature.id}
            data-layer="border"
            d={multiPolygonToPath(feature.coordinates, projection)}
            fill="none"
            stroke={StyleConstants.GREY_COLOR}
            strokeWidth={2}
          />
        ))}
    </g>
  );
};

export default React.memo(LayerBackdrop);
